import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { seatMap } from '@eti/products';
import { useProperty, useSearch, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Collection, DevBar } from '@eti/debug';
import { Helmet } from 'react-helmet';
import has from 'lodash/has';
import type { GetAncillaryBundleQuery, SearchOnResultPageQuery, SortType } from '@eti/schema-types';
import { isDev } from '../../../common/debug/utils';
import { DebugCommonComponents, DebugGraphQlResponses } from '../../../common/debug/components';
import { useKindly } from '../../../common/custom-hooks/useKindly';
import { routes } from '../../../constants/routesAndApisConstants';
import { useToggle } from '../../../common/custom-hooks/toggle';
import { RESULT } from '../../../constants/formNamesConstants';
import { useSearchValuesToDataLayerEffect } from '../../../common/data-layer/dataLayer';
import useSelfServiceRebooking from '../../../common/self-service-rebooking/useSelfServiceRebooking';
import { getRebookingData } from '../../start/utils/rebooking';
import { SELECT_TRIP_WITH_DATA, SELECT_TRIP } from '../graphql/mutations';
import { transformSearchResultData } from '../utils/searchDetails';
import { collectFlightsAndPassengersContextData } from '../../../common/tracking/snowplow/snowplowUtils';
import { useSkyscannerLandingTracking } from '../../../common/tracking/skyscannerTracking';
import { useGetAncillaryBundle } from '../utils/ancillaryBundleUtils';
import useSaveTrips from '../../../common/custom-hooks/save-trips/useSaveTrips';
import { RESULT_PAGE_VERSION_3 } from '../constants/resultConstants';
import type { SelectedTripPropsType, SelectTripWithDataSelectedTripType } from '../models';
import { OFFERED_PRODUCTS } from '../../traveler-details/constants/formFields';
import { useGetBlue, eventTypes } from '../../../utils/useGetBlue';
import AncillaryBundleModal from './AncillaryBundleModal';
import NoSearchPossibleModal from './NoSearchPossibleModal';
import SearchTransition from './SearchTransition';
import { resetAllFilters } from './search-filters/utils/searchFilters';
import { ResultPageContextProvider } from './v3/contexts/ResultPageContext';
import ResultSkeletonV3 from './v3/skeleton/ResultSkeleton';

const ResultPageContentV1 = lazy(() => import('./v1/ResultPageContent'));
const ResultPageContentV3 = lazy(() => import('./v3/ResultPageContent'));

const shouldCallSelectTripWithData = (
  isResultPageSelectTripCalled: boolean,
  resultSelectionKey: string,
  isKeepSelectionOnTopEnabled: boolean,
  selectedTripSelectionKey: string | null,
) => {
  if (isResultPageSelectTripCalled || !resultSelectionKey) {
    return false;
  }
  if (selectedTripSelectionKey) {
    return isKeepSelectionOnTopEnabled;
  }
  return true;
};

interface ResultPageProps {
  selectionKey: string;
}

export type Props = ResultPageProps & InjectedFormProps<{}, ResultPageProps>;

type HistoryState = {
  isMetaEntrySelectedTripFailure?: boolean;
};

const ResultPage = ({ selectionKey }: Props) => {
  const history = useHistory<HistoryState>();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { currentSearchData, setSearchData: updateSearchData, setIsSearchComplete } = useSearch();
  const [shouldShowTripIsNotAvailableModal, setShowTripIsNotAvailableModal] = useState(false);
  const [selectedTripData, setSelectedTripData] =
    useState<null | SelectTripWithDataSelectedTripType>(null);
  const [shouldShowSelectedTrip, setShouldShowSelectedTrip] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchData, setSearchData] = useState<SearchOnResultPageQuery | null>(null);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  useKindly();
  const [isInitialTripSelectionLoading, setIsInitialTripSelectionLoading] = useState(false);
  useSearchValuesToDataLayerEffect(currentSearchData);
  const [pageTracked, setPageTracked] = useState(false);
  const [showAddTripLoader, setShowAddTripLoader] = useState(false);
  const [shouldSkipSuccessCallForV3, setShouldSkipSuccessCallForV3] = useState(false);
  const { removeSavedTrip } = useSaveTrips();
  const shouldShowTrustInformation =
    searchData && !isInitialTripSelectionLoading && p('Result.TrustInformation.Enabled');

  const resultPageVersion = String(p('Result.Page.Version'));
  const isSkeletonEnabled = p('Result.Skeleton.Enabled');

  const isResultPageV3 = resultPageVersion === RESULT_PAGE_VERSION_3;
  const {
    isOpen: isAncillaryBundleModalOpen,
    handleOpen: openAncillaryBundleModal,
    handleClose: closeAncillaryBundleModal,
  } = useToggle(false);

  const handleSort = (sortType?: SortType) => {
    if (updateSearchData) {
      updateSearchData({ selectedSortType: sortType });
    }
  };

  const { isSnowplowTrackingEnabled, sendTrackEvent, updateSnowplowContextData } =
    useSnowplowTracking();

  const route = currentSearchData?.routes?.[0];
  const originAndDestinationCodes =
    route?.originCode && route?.destinationCode
      ? `${route.originCode}${route.destinationCode}`
      : '';

  useGetBlue(eventTypes.PRODUCT, originAndDestinationCodes);

  useEffect(() => {
    sessionStorage.setItem('brandedFareOffered', 'false');
  }, []);

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      if (currentSearchData?.routes) {
        updateSnowplowContextData({
          ...collectFlightsAndPassengersContextData(currentSearchData, selectedTripData),
        });
      }

      const shouldSendTrackEvent = (!selectionKey || selectedTripData) && !pageTracked;

      if (shouldSendTrackEvent) {
        sendTrackEvent({ category: 'Results', action: 'Seen', label: 'Page' });
        setPageTracked(true);
      }
    }
  }, [
    isSnowplowTrackingEnabled,
    pageTracked,
    currentSearchData,
    selectionKey,
    selectedTripData,
    sendTrackEvent,
    updateSnowplowContextData,
  ]);

  useEffect(() => {
    sessionStorage.setItem(
      'showSeatMapStep',
      String(p('IbeClient.TravelerDetails.Modal') === 'SEATMAP'),
    );
  }, [p]);

  useEffect(() => {
    if (setIsSearchComplete) {
      setIsSearchComplete(Boolean(searchData));
    }
  }, [setIsSearchComplete, searchData]);

  const { isSelfServiceRebooking, selfServiceRebookingTravelers } = useSelfServiceRebooking();
  const rebookingData = isSelfServiceRebooking
    ? getRebookingData(selfServiceRebookingTravelers)
    : null;

  const {
    isOpen: isFiltersPanelOpen,
    handleClose: closeFiltersPanel,
    handleToggle: toggleFiltersPanel,
  } = useToggle(false);

  const [selectTripWithData, { called: isSelectTripCalled }] = useMutation(SELECT_TRIP_WITH_DATA, {
    onCompleted: ({ selectTrip: selectedTripResponse }) => {
      if (
        selectedTripResponse.success &&
        !shouldSkipSuccessCallForV3 &&
        selectedTripResponse.selectedTrip
      ) {
        setSelectedTripData(selectedTripResponse.selectedTrip);
      } else if (!selectedTripResponse.success) {
        setShowTripIsNotAvailableModal(true);
        const selectionKeyUrlParam = new URLSearchParams(history.location.search).get(
          'selectionKey',
        );
        if (selectionKeyUrlParam) {
          removeSavedTrip(selectionKeyUrlParam);
        }
      }
      setIsInitialTripSelectionLoading(false);
    },
    onError: () => {
      setShowTripIsNotAvailableModal(true);
      setIsInitialTripSelectionLoading(false);
      const selectionKeyUrlParam = new URLSearchParams(history.location.search).get('selectionKey');
      if (selectionKeyUrlParam) {
        removeSavedTrip(selectionKeyUrlParam);
      }
    },
  });

  const [selectTrip] = useMutation(SELECT_TRIP, {
    onCompleted: ({ selectTrip: selectedTripResponse }) => {
      if (!selectedTripResponse.success) {
        setShowTripIsNotAvailableModal(true);
      }
    },
    onError: () => {
      setShowTripIsNotAvailableModal(true);
    },
  });

  const handleSearch = (keepFilters: boolean) => {
    if (!keepFilters && updateSearchData) {
      updateSearchData({ filters: { ...resetAllFilters(currentSearchData?.filters) } });
    }
    closeFiltersPanel();
    history.replace({ ...history.location });
    setSelectedTripData(null);
    setShouldShowSelectedTrip(false);
    setShouldRefetch(true);
  };

  const handleShowValidWithVoucher = (shouldShowValidWithVoucher: boolean) => {
    if (updateSearchData) {
      updateSearchData({ shouldOnlyShowFlightsValidWithVoucher: shouldShowValidWithVoucher });
    }
    handleSearch(true);
  };

  const handleSearchData = useCallback(
    (data) => {
      const selectedTripSelectionKey = sessionStorage.getItem('selectedTripSelectionKey');
      const resultSelectionKey = selectedTripSelectionKey || selectionKey;
      const isKeepSelectionOnTopEnabled = p('Result.KeepSelectionOnTop.Enabled');
      setSearchData(data);

      const replaceHistoryLocationState = () => {
        history.replace({
          ...history.location,
          state: {
            ...history.location.state,
            isMetaEntrySelectedTripFailure: false,
          },
        });
      };

      if (history.location.state?.isMetaEntrySelectedTripFailure) {
        setShowTripIsNotAvailableModal(true);
        replaceHistoryLocationState();
      } else if (
        shouldCallSelectTripWithData(
          isSelectTripCalled,
          resultSelectionKey,
          isKeepSelectionOnTopEnabled,
          selectedTripSelectionKey,
        )
      ) {
        setIsInitialTripSelectionLoading(true);
        selectTripWithData({
          variables: {
            selectionKey: resultSelectionKey,
            searchPath: data?.search?.searchPath,
            excludedExtraProductIds: [seatMap.id.toString()],
          },
        });

        setShouldShowSelectedTrip(true);
      }
    },
    [history, isSelectTripCalled, p, selectionKey, selectTripWithData],
  );

  const isNewStoreStatisticSol = p('Offered.Product.Cache.Enabled');
  const redirectToTravelerDetails = () => {
    if (isNewStoreStatisticSol) {
      sessionStorage.removeItem(OFFERED_PRODUCTS);
    }
    history.push(routes.TRAVELER_DETAILS);
  };

  const redirectToBookingStepPage = () => {
    if (isNewStoreStatisticSol) {
      sessionStorage.removeItem(OFFERED_PRODUCTS);
    }
    history.push(routes.STEP_TWO);
  };

  const shouldRedirectToYourSelectedTrip = (
    selectedTrip: SelectTripWithDataSelectedTripType,
    ancillaryBundleData: GetAncillaryBundleQuery,
  ) => {
    const showBrandedFaresOnYourSelectedTrip =
      selectedTrip?.brandedFares?.length && p('BrandedFares.Enabled');
    const showAncillaryBundleOnYourSelectedTrip =
      ancillaryBundleData?.ancillaryBundle?.length > 0 &&
      p('YourSelectedTrip.AncillaryBundle.Enabled');
    return (
      p('YourSelectedTripPage.AfterResult.Enabled') &&
      (showBrandedFaresOnYourSelectedTrip || showAncillaryBundleOnYourSelectedTrip)
    );
  };

  const redirectToYourSelectedTrip = () => {
    history.push(routes.YOUR_SELECTED_TRIP);
  };
  const getAncillaryBundle = useGetAncillaryBundle(selectionKey);
  const shouldCallgetAncillaryBundle =
    p('YourSelectedTripPage.AfterResult.Enabled') && p('YourSelectedTrip.AncillaryBundle.Enabled');

  const onAddTripComplete = async (
    callback: () => void,
    hasPreSelectedProducts: boolean,
    ancillaryBundleData: GetAncillaryBundleQuery,
    selectedTrip: SelectTripWithDataSelectedTripType,
    shouldSetSelectedTripDataInResultV3: boolean,
  ) => {
    if (
      !hasPreSelectedProducts &&
      shouldRedirectToYourSelectedTrip(selectedTrip, ancillaryBundleData)
    ) {
      redirectToYourSelectedTrip();
    } else if (callback) {
      await callback();
      if (shouldSetSelectedTripDataInResultV3 && isResultPageV3) {
        setSelectedTripData(selectedTrip);
      }
    } else {
      redirectToTravelerDetails();
    }
  };

  const addTrip = async (
    selectTripProps: SelectedTripPropsType,
    callback: () => void,
    hasPreSelectedProducts = false,
  ) => {
    setShowAddTripLoader(true);

    if (isResultPageV3) {
      setShouldSkipSuccessCallForV3(true);
    }

    try {
      const { data } = await selectTripWithData({
        variables: {
          ...selectTripProps.variables,
          searchPath: searchData?.search?.searchPath,
          excludedExtraProductIds: [seatMap.id.toString()],
        },
      });

      const { data: ancillaryBundleData }: { data: GetAncillaryBundleQuery } =
        shouldCallgetAncillaryBundle && (await getAncillaryBundle());

      if (!data?.selectTrip.success) {
        setShowTripIsNotAvailableModal(true);
        return;
      }

      if (data.selectTrip.selectedTrip?.selectionKey) {
        sessionStorage.setItem(
          'selectedTripSelectionKey',
          data.selectTrip.selectedTrip.selectionKey,
        );
      }

      await onAddTripComplete(
        callback,
        hasPreSelectedProducts,
        ancillaryBundleData,
        data.selectTrip.selectedTrip,
        true,
      );
    } catch {
      setShowTripIsNotAvailableModal(true);
    } finally {
      setShowAddTripLoader(false);
    }
  };

  const addTripForSelectedTrip = async (
    selectTripProps: SelectedTripPropsType,
    callback: () => void,
    hasPreSelectedProducts = false,
  ) => {
    setShowAddTripLoader(true);
    try {
      const { data } = await selectTrip({
        variables: {
          ...selectTripProps.variables,
          searchPath: searchData?.search?.searchPath,
        },
      });
      const { data: ancillaryBundleData } =
        shouldCallgetAncillaryBundle && (await getAncillaryBundle());

      if (!data?.selectTrip.success) {
        setShowTripIsNotAvailableModal(true);
        return;
      }

      await onAddTripComplete(
        callback,
        hasPreSelectedProducts,
        ancillaryBundleData,
        selectedTripData,
        false,
      );
    } catch {
      setShowTripIsNotAvailableModal(true);
    } finally {
      setShowAddTripLoader(false);
    }
  };

  const transformedSearchResultData = useMemo(
    () =>
      searchData && currentSearchData
        ? transformSearchResultData(currentSearchData, searchData.search)
        : null,
    [currentSearchData, searchData],
  );

  useSkyscannerLandingTracking(selectedTripData);

  if (p('Result.ShowNoSearchModal.Enabled') && !has(currentSearchData, 'origin')) {
    return <NoSearchPossibleModal />;
  }

  const resultPageContent = isResultPageV3 ? (
    <ResultPageContextProvider
      addTrip={addTrip}
      addTripForSelectedTrip={addTripForSelectedTrip}
      closeFiltersPanel={closeFiltersPanel}
      handleSearch={handleSearch}
      handleSearchData={handleSearchData}
      handleShowValidWithVoucher={handleShowValidWithVoucher}
      handleSort={handleSort}
      isFiltersPanelOpen={isFiltersPanelOpen}
      isInitialTripSelectionLoading={isInitialTripSelectionLoading}
      isSearchLoading={isSearchLoading}
      isSelectTripLoading={showAddTripLoader}
      isSelfServiceRebooking={isSelfServiceRebooking}
      onShowAncillaryBundleModal={openAncillaryBundleModal}
      rebookingData={rebookingData}
      redirectToDBF={redirectToBookingStepPage}
      redirectToTravelerDetails={redirectToTravelerDetails}
      searchData={transformedSearchResultData}
      selectedTripData={selectedTripData}
      setIsSearchLoading={setIsSearchLoading}
      setShouldRefetch={setShouldRefetch}
      setShowTripIsNotAvailableModal={setShowTripIsNotAvailableModal}
      shouldRefetch={shouldRefetch}
      shouldShowSelectedTrip={shouldShowSelectedTrip}
      shouldShowTripIsNotAvailableModal={shouldShowTripIsNotAvailableModal}
      toggleFiltersPanel={toggleFiltersPanel}
    >
      <ResultPageContentV3 />
    </ResultPageContextProvider>
  ) : (
    <ResultPageContentV1
      addTrip={addTrip}
      addTripForSelectedTrip={addTripForSelectedTrip}
      closeFiltersPanel={closeFiltersPanel}
      handleSearch={handleSearch}
      handleSearchData={handleSearchData}
      handleShowValidWithVoucher={handleShowValidWithVoucher}
      handleSort={handleSort}
      isFiltersPanelOpen={isFiltersPanelOpen}
      isInitialTripSelectionLoading={isInitialTripSelectionLoading}
      isSearchLoading={isSearchLoading}
      isSelectTripLoading={showAddTripLoader}
      isSelfServiceRebooking={isSelfServiceRebooking}
      onShowAncillaryBundleModal={openAncillaryBundleModal}
      rebookingData={rebookingData}
      redirectToDBF={redirectToBookingStepPage}
      redirectToTravelerDetails={redirectToTravelerDetails}
      searchData={transformedSearchResultData}
      selectedTripData={selectedTripData}
      setIsSearchLoading={setIsSearchLoading}
      setShouldRefetch={setShouldRefetch}
      setShowTripIsNotAvailableModal={setShowTripIsNotAvailableModal}
      shouldRefetch={shouldRefetch}
      shouldShowSelectedTrip={shouldShowSelectedTrip}
      shouldShowTripIsNotAvailableModal={shouldShowTripIsNotAvailableModal}
      shouldShowTrustInformation={shouldShowTrustInformation}
      toggleFiltersPanel={toggleFiltersPanel}
    />
  );

  const FallBackComponent = () => {
    return isResultPageV3 && isSkeletonEnabled ? <ResultSkeletonV3 /> : <SearchTransition />;
  };

  return (
    <>
      <Helmet title={t('PageTitle.rf.air.result.action')} />
      <AncillaryBundleModal
        addTripForSelectedTrip={addTripForSelectedTrip}
        form={RESULT}
        isOpen={isAncillaryBundleModalOpen}
        onClose={closeAncillaryBundleModal}
        selectedExtraProducts={selectedTripData && selectedTripData.availableExtraProducts}
        travelers={selectedTripData?.tripTravelers ? selectedTripData.tripTravelers : []}
      />
      <Suspense fallback={<FallBackComponent />}>{resultPageContent}</Suspense>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugGraphQlResponses />
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

export default reduxForm<{}, ResultPageProps>({
  form: RESULT,
})(ResultPage);
